<template>
  <div
    class="cui-custom-progress relative w-full h-16 bg-gray-400 cursor-pointer rounded-xl mt-4"
    @click="seek"
    @pointerdown="handleTimelinePointerDown"
    ref="barRef"
    v-on-click-outside="stopEdit"
    data-onboarding-video-snipping="progress-bar"
  >
    <div class="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
      <div class="relative h-full w-full">
        <div
          v-for="tile in timelineThumbnailTiles"
          :key="tile.id"
          :class="[
            'absolute top-0 h-full bg-gray-600/60 bg-center bg-cover bg-no-repeat transition-opacity duration-300',
            tile.imageSrc ? 'opacity-100' : 'opacity-0'
          ]"
          :style="{
            left: `${tile.leftPx}px`,
            width: `${tile.widthPx}px`,
            backgroundImage: tile.imageSrc ? `url(${tile.imageSrc})` : 'none'
          }"
        />
        <div class="cui-custom-progress-track absolute-cover bg-gray-500/40"></div>
      </div>
    </div>

    <template v-if="hasTimelineDuration">
      <video-editor-timeline-clip
        v-for="clip in clipsToPlot"
        :key="clip.id"
        :class="{
          'z-10': String(clip?.id) === String(activeEditClipId)
        }"
        :clip="clip"
        :selected="String(clip?.id) === String(activeEditClipId)"
        :video-duration="videoDuration"
        :active-marker-id="editingMarker?.id ?? null"
        :marker-nudges="markerNudgesById"
        @select-clip="selectClip"
        @select-marker="selectMarker"
        @open-clip-menu="openClipMenu"
      />
    </template>
    <template v-else-if="clipsToPlot.length">
      <div
        v-for="(clip, idx) in clipsToPlot"
        :key="`timeline-loading-${clip?.id || idx}`"
        class="timeline-clip-loading absolute top-1 bottom-1 rounded-xl pointer-events-none"
        :style="timelineLoadingClipStyle(idx, clipsToPlot.length)"
      />
    </template>

    <div
      class="cui-custom-progress-current-position absolute w-0.5 -ml-px h-full bg-white pointer-events-none z-20"
      :style="{ left: `${progress * 100}%` }"
      data-onboarding-video-snipping="current-position"
    >
      <div class="cui-custom-progress-handle absolute -top-1 left-1/2 w-3 h-3 bg-white -translate-x-1/2 border border-1 border-gray rounded-full" />
    </div>

    <video-editor-clip-actions-dropdown
      ref="clipActionsMenuRef"
      :clip="contextMenuClip"
      context
      :popper="{ placement: 'right-start', offsetDistance: 0 }"
    />

  </div>

</template>

<script setup>
  import debounce from 'lodash.debounce';
  import throttle from 'lodash.throttle';
  import {storeToRefs} from 'pinia';
  import {vOnClickOutside} from '@vueuse/components'

  const props = defineProps({
    disableEdit: Boolean,
    player: Object, // video.js instance
    videoSrc: String,
    initialDurationSeconds: {
      type: Number,
      default: 0
    }
  });
  const emit = defineEmits(['current-time-change']);

  const barRef = ref(null);
  const progress = ref(0);
  const {width: barWidth, height: barHeight} = useElementSize(barRef);

  const videoEditorStore = useVideoEditorStore();
  const {modals} = useFModals();
  const {deleteClip: deleteClipAction} = useVideoEditorActions();
  const {clips, activeEditClipId} = storeToRefs(videoEditorStore);
  const {
    thumbnails: timelineThumbnails,
    sourceAspectRatio,
    generateStrip: generateThumbnailStrip,
    cancel: cancelThumbnailGeneration,
    dispose: disposeTimelineThumbnails
  } = useTimelineThumbnails();
  const TIMELINE_CAPTURE_COUNT = 27;
  const TIMELINE_FALLBACK_CAPTURE_WIDTH_PX = 56;
  const TIMELINE_MIN_VISIBLE_FRAME_WIDTH_FRACTION = 0.93;
  const MARKER_THUMB_WIDTH_PX = 48;
  const MARKER_THUMB_GAP_PX = 4;
  const MARKER_SNAPSHOT_THROTTLE_MS = 140;
  const CLIP_EDGE_HANDLE_WIDTH_PX = 24;
  const isCustomFrameEditorOpen = computed(() =>
    modals.value.some(modal => modal.id === VIDEO_EDITOR_CUSTOM_FRAME_EDITOR_MODAL_ID)
  );

  const timelineThumbnailTiles = computed(() => {
    const captures = timelineThumbnails.value;
    const width = Math.max(0, Math.floor(barWidth.value || 0));
    const height = Math.max(1, Math.floor(barHeight.value || 64));

    if (!captures.length || !width) {
      return [];
    }

    const aspect = Math.max(0.1, Number(sourceAspectRatio.value) || (16 / 9));
    const naturalTileWidthPx = Math.max(16, Math.round(height * aspect));
    const minVisibleStepPx = naturalTileWidthPx * TIMELINE_MIN_VISIBLE_FRAME_WIDTH_FRACTION;
    const tileWidthPx = naturalTileWidthPx;

    // Wide timelines can repeat frames to fill width.
    if (width >= naturalTileWidthPx * captures.length) {
      const tileCount = Math.max(captures.length, Math.ceil(width / naturalTileWidthPx));
      const maxLeftPx = Math.max(0, width - tileWidthPx);
      const stepPx = tileCount > 1 ? (maxLeftPx / (tileCount - 1)) : 0;

      return Array.from({length: tileCount}, (_, tileIndex) => {
        const captureIndex = Math.min(captures.length - 1, Math.floor((tileIndex / tileCount) * captures.length));
        const capture = captures[captureIndex];

        return {
          id: `tile-${tileIndex}-${capture.id}`,
          leftPx: Math.round(tileIndex * stepPx * 100) / 100,
          widthPx: tileWidthPx,
          imageSrc: capture.imageSrc
        };
      });
    }

    // Narrow timelines: drop frames first so overlap never hides more than the configured amount.
    const maxLeftPx = Math.max(0, width - tileWidthPx);
    const maxDisplayCountByVisibility = maxLeftPx > 0
      ? Math.floor(maxLeftPx / Math.max(1, minVisibleStepPx)) + 1
      : 1;
    const displayCount = Math.min(captures.length, Math.max(1, maxDisplayCountByVisibility));
    const stepPx = displayCount > 1 ? (maxLeftPx / (displayCount - 1)) : 0;

    return Array.from({length: displayCount}, (_, tileIndex) => {
      const captureIndex = displayCount === 1
        ? 0
        : Math.min(captures.length - 1, Math.round((tileIndex / (displayCount - 1)) * (captures.length - 1)));
      const capture = captures[captureIndex];

      return {
        id: `tile-${tileIndex}-${capture.id}`,
        leftPx: Math.round(tileIndex * stepPx * 100) / 100,
        widthPx: tileWidthPx,
        imageSrc: capture.imageSrc
      };
    });
  });

  const clipsToPlot = computed(() =>
    clips.value.slice().sort((a, b) => (Math.min(...a.markers.map(m => m.time)) - Math.min(...b.markers.map(m => m.time))))
  );

  const markerNudgesById = computed(() => {
    const width = Math.max(0, Math.floor(barWidth.value || 0));
    const duration = Number(videoDuration.value) || 0;
    if (!width || !duration) {
      return {};
    }

    const maxRequiredSpacingPx = MARKER_THUMB_WIDTH_PX + MARKER_THUMB_GAP_PX;
    const halfThumbPx = MARKER_THUMB_WIDTH_PX / 2;
    const minCenterX = halfThumbPx;
    const maxCenterX = Math.max(halfThumbPx, width - halfThumbPx);

    const markers = clipsToPlot.value
      .flatMap(clip => (clip?.markers || []).map(marker => ({
        id: marker?.id,
        idealX: (Number(marker?.time) / duration) * width
      })))
      .filter(item => item.id && Number.isFinite(item.idealX))
      .sort((a, b) => (a.idealX - b.idealX) || String(a.id).localeCompare(String(b.id)));

    if (!markers.length) {
      return {};
    }

    const feasibleSpacingPx = Math.min(
      maxRequiredSpacingPx,
      (maxCenterX - minCenterX) / Math.max(1, markers.length - 1)
    );

    // Solve globally with bounded isotonic regression on transformed coordinates:
    // x_i = y_i + i*spacing, with nondecreasing y_i and boundary-aware bounds.
    const blocks = markers.map((marker, idx) => {
      const transformed = marker.idealX - (idx * feasibleSpacingPx);
      const lower = minCenterX - (idx * feasibleSpacingPx);
      const upper = maxCenterX - (idx * feasibleSpacingPx);
      const clampedUpper = Math.max(lower, upper);

      return {
        start: idx,
        end: idx,
        sum: transformed,
        count: 1,
        lower,
        upper: clampedUpper,
        value: Math.min(Math.max(transformed, lower), clampedUpper)
      };
    });

    let blockIdx = 0;
    while (blockIdx < blocks.length - 1) {
      if (blocks[blockIdx].value <= blocks[blockIdx + 1].value) {
        blockIdx++;
        continue;
      }

      const left = blocks[blockIdx];
      const right = blocks[blockIdx + 1];
      const lower = Math.max(left.lower, right.lower);
      const upper = Math.max(lower, Math.min(left.upper, right.upper));
      const sum = left.sum + right.sum;
      const count = left.count + right.count;
      const mean = sum / count;

      blocks.splice(blockIdx, 2, {
        start: left.start,
        end: right.end,
        sum,
        count,
        lower,
        upper,
        value: Math.min(Math.max(mean, lower), upper)
      });

      if (blockIdx > 0) {
        blockIdx--;
      }
    }

    const adjustedCenters = Array.from({length: markers.length}, () => 0);
    for (const block of blocks) {
      for (let idx = block.start; idx <= block.end; idx++) {
        adjustedCenters[idx] = block.value + (idx * feasibleSpacingPx);
      }
    }

    return Object.fromEntries(markers.map((marker, idx) => [marker.id, adjustedCenters[idx] - marker.idealX]));
  });

  const editingMarker = ref(null);
  const contextMenuClip = ref(null);
  const clipActionsMenuRef = ref(null);
  const hasMetadataDuration = ref(false);
  const editingClip = computed({
    get() {
      if (activeEditClipId.value === null || activeEditClipId.value === undefined) {
        return null;
      }

      return clipsToPlot.value.find(clip => String(clip?.id) === String(activeEditClipId.value)) || null;
    },
    set(clip) {
      videoEditorStore.setActiveEditClip({clipId: clip?.id ?? null});
    }
  });

  const videoDuration = ref(Math.max(
    Number(props.initialDurationSeconds) || 0,
    Number(props.player?.duration?.()) || 0
  ));
  const hasTimelineDuration = computed(() => Number(videoDuration.value) > 0);

  function syncDurationFromPlayer() {
    const nextDuration = Number(props.player?.duration?.()) || 0;
    if (!nextDuration) {
      return;
    }

    hasMetadataDuration.value = true;
    videoDuration.value = nextDuration;
  }

  function getDurationSec() {
    return Number(props.player?.duration?.()) || Number(videoDuration.value) || 0;
  }

  function getClipMarkerRange(clip) {
    const markerTimes = (clip?.markers || [])
      .map(marker => Number(marker?.time))
      .filter(time => Number.isFinite(time));

    if (markerTimes.length < 2) {
      return null;
    }

    const start = Math.min(...markerTimes);
    const end = Math.max(...markerTimes);
    if (start === end) {
      return null;
    }

    return {
      start,
      end
    };
  }

  function timelineLoadingClipStyle(index, totalCount) {
    const widths = [28, 20, 16, 24];
    const widthPercent = widths[index % widths.length];
    const maxLeft = Math.max(0, 100 - widthPercent);
    const step = totalCount > 1 ? maxLeft / (totalCount - 1) : 0;
    const leftPercent = Math.min(maxLeft, step * index);

    return {
      left: `${leftPercent}%`,
      width: `${widthPercent}%`
    };
  }

  watch(
    () => props.initialDurationSeconds,
    (nextDuration) => {
      const normalized = Number(nextDuration) || 0;
      if (!hasMetadataDuration.value && normalized > 0) {
        videoDuration.value = normalized;
      }
    },
    {immediate: true}
  );

  const regenerateTimelineThumbnails = debounce(async () => {
    if (!props.videoSrc || !videoDuration.value) {
      cancelThumbnailGeneration();
      return;
    }

    await generateThumbnailStrip({
      src: props.videoSrc,
      duration: videoDuration.value,
      captureCount: TIMELINE_CAPTURE_COUNT,
      thumbHeightPx: Math.max(16, Math.floor(barHeight.value || 64)),
      thumbWidthPx: TIMELINE_FALLBACK_CAPTURE_WIDTH_PX
    });
  }, 150);

  let hammer = null;
  let barRect = null;
  let dragOffset = 0;
  let isDragging = false;
  let dragMode = 'seek';
  let draggingClip = null;
  let clipDragAppliedDelta = 0;
  let markerDragTimeOffsetSec = 0;
  let ignoreSeekClickUntilMs = 0;

  const queueMarkerSnapshotRefresh = throttle((marker) => {
    if (!marker) {
      return;
    }

    void videoEditorStore.refreshMarkerSnapshot({
      marker,
      time: marker.time
    });
  }, MARKER_SNAPSHOT_THROTTLE_MS, {
    leading: true,
    trailing: true
  });

  const queueClipSnapshotRefresh = throttle((clip) => {
    if (!clip) {
      return;
    }

    void videoEditorStore.refreshClipMarkerSnapshots({clip});
  }, MARKER_SNAPSHOT_THROTTLE_MS, {
    leading: true,
    trailing: true
  });

  // Update progress from player (unless dragging)
  function updateProgress() {
    if (isDragging) {
      return;
    }

    const duration = getDurationSec();
    if (props.player && duration) {
      const nextTime = Number(props.player.currentTime()) || 0;
      progress.value = nextTime / duration;
      emit('current-time-change', nextTime);
    }
  }

  function seek(event) {
    if (Date.now() < ignoreSeekClickUntilMs) {
      return;
    }

    stopEdit();

    barRect = barRef.value.getBoundingClientRect();
    const offsetX = event.clientX - barRect.left;
    progress.value = Math.max(0, Math.min(1, offsetX / barRect.width));

    syncPlayerToProgress(progress.value);
  }

  function getPointerClientX(event) {
    const clientX = Number(
      event?.clientX
      ?? event?.touches?.[0]?.clientX
      ?? event?.changedTouches?.[0]?.clientX
    );

    return Number.isFinite(clientX) ? clientX : null;
  }

  function isTimelineInteractiveChild(target) {
    return Boolean(
      target?.closest?.('[data-drag-clip-id][data-drag-marker-id], [data-clip-region-id], [data-marker-id]')
    );
  }

  function handleTimelinePointerDown(event) {
    if (Date.now() < ignoreSeekClickUntilMs) {
      return;
    }

    if (isTimelineInteractiveChild(event?.target)) {
      return;
    }

    const clientX = getPointerClientX(event);
    if (clientX === null) {
      return;
    }

    barRect = barRef.value?.getBoundingClientRect?.() || null;
    if (!barRect?.width) {
      return;
    }

    syncPlayerToProgress((clientX - barRect.left) / barRect.width);
  }

  function startEdit({clip, at, syncPlayer = true}) {
    if (props.disableEdit) {
      return;
    }

    editingClip.value = clip;
    editingMarker.value = at;

    videoEditorStore.setActiveEditMarker({
      clipId: clip?.id ?? null,
      markerId: at?.id ?? null
    });

    if (syncPlayer) {
      props.player.currentTime(at.time);
      updateProgress();
    }
  }

  function isToolbarTarget(target) {
    return Boolean(target?.closest?.('[data-onboarding-video-snipping="tool-bar"]'));
  }

  function stopEdit(event) {
    if (event && isToolbarTarget(event.target)) {
      return;
    }

    editingClip.value = null;
    editingMarker.value = null;
    videoEditorStore.clearActiveEditMarker();
  }

  function selectClip({clip, clientX = null}) {
    if (props.disableEdit) {
      return;
    }

    editingClip.value = clip;
    editingMarker.value = null;
    videoEditorStore.clearActiveEditMarker();

    if (!Number.isFinite(clientX) || Date.now() < ignoreSeekClickUntilMs) {
      return;
    }

    barRect = barRef.value?.getBoundingClientRect?.() || null;
    if (!barRect?.width) {
      return;
    }

    syncPlayerToProgress((clientX - barRect.left) / barRect.width);
  }

  function selectMarker({clip, marker}) {
    if (props.disableEdit || !clip || !marker) {
      return;
    }

    startEdit({
      clip,
      at: marker
    });
  }

  async function openClipMenu({clip, clientX, clientY}) {
    if (!clip) {
      return;
    }

    if (!props.disableEdit) {
      selectClip({clip});
    }

    contextMenuClip.value = clip;
    await nextTick();
    clipActionsMenuRef.value?.open({
      clientX,
      clientY
    });
  }

  function findClipAndMarker({clipId, markerId}) {
    const clip = clipsToPlot.value.find(item => item.id === clipId);
    const marker = clip?.markers?.find(item => item.id === markerId);

    if (!clip || !marker) {
      return null;
    }

    return {
      clip,
      marker
    };
  }

  function getClipBoundaryMarkers(clip) {
    const sortedMarkers = (clip?.markers || []).slice().sort((a, b) => Number(a.time) - Number(b.time));
    const clipStart = sortedMarkers[0] || null;
    const clipEnd = sortedMarkers[sortedMarkers.length - 1] || null;

    return {
      clipStart,
      clipEnd
    };
  }

  function resolveClipEdgeMarker({clip, clipElement, pointerClientX}) {
    if (!clip || !clipElement || !Number.isFinite(pointerClientX)) {
      return null;
    }

    const {clipStart, clipEnd} = getClipBoundaryMarkers(clip);
    if (!clipStart || !clipEnd) {
      return null;
    }

    const clipRect = clipElement.getBoundingClientRect();
    const clipWidth = Number(clipRect.width) || 0;
    if (!clipWidth) {
      return null;
    }

    const localX = pointerClientX - clipRect.left;
    const edgeHandleWidth = Math.max(8, Math.min(CLIP_EDGE_HANDLE_WIDTH_PX, clipWidth / 2));

    if (localX <= edgeHandleWidth) {
      return clipStart;
    }

    if (localX >= clipWidth - edgeHandleWidth) {
      return clipEnd;
    }

    return null;
  }

  async function startMarkerDrag({clip, marker, pointerClientX}) {
    if (!clip || !marker) {
      return false;
    }

    dragMode = 'marker';

    startEdit({
      clip,
      at: marker,
      syncPlayer: false
    });

    await nextTick();

    const duration = getDurationSec();
    const pointerRatioAtStart = barRect?.width
      ? Math.max(0, Math.min(1, (pointerClientX - barRect.left) / barRect.width))
      : 0;
    const pointerTimeAtStart = pointerRatioAtStart * duration;
    markerDragTimeOffsetSec = Number(marker.time) - pointerTimeAtStart;

    dragOffset = 0;
    isDragging = true;
    return true;
  }

  function nudgeCurrentTime(delta) {
    const duration = getDurationSec();
    if (!duration) {
      return;
    }

    const currentTime = Number(props.player.currentTime()) || 0;
    const nextTime = Math.max(0, Math.min(duration, currentTime + delta));
    props.player.currentTime(nextTime);
    updateProgress();
  }

  function syncPlayerToProgress(nextProgress) {
    const duration = getDurationSec();
    if (!duration) {
      return;
    }

    const normalizedProgress = Math.max(0, Math.min(1, Number(nextProgress) || 0));
    const nextTime = normalizedProgress * duration;
    progress.value = normalizedProgress;
    emit('current-time-change', nextTime);
    props.player.currentTime(nextTime);
  }

  function nudgeClip(clip, delta) {
    const duration = getDurationSec();
    if (!clip || !duration) {
      return;
    }

    videoEditorStore.moveClip({
      clip,
      delta,
      minTime: 0,
      maxTime: duration
    });
  }

  function nudgeMarker(marker, delta) {
    if (!marker) {
      return;
    }

    const duration = getDurationSec();
    const didAdjust = videoEditorStore.adjustMarker({
      marker,
      time: marker.time + delta,
      maxTime: duration || Infinity
    });

    if (!didAdjust) {
      return;
    }

    props.player.currentTime(marker.time);
    updateProgress();
  }

  function confirmDeleteClip(clip) {
    useConfirmModal({
      heading: 'Delete Clip',
      message: 'Are you sure you want to delete this clip?',
      confirmText: 'Delete',
      color: 'red',
      confirmMethod: async () => {
        try {
          const deletingEditingMarker = Boolean(
            editingMarker.value
            && clip?.markers?.some(marker => marker?.id === editingMarker.value?.id)
          );
          await deleteClipAction({clip});
          if (deletingEditingMarker) {
            editingMarker.value = null;
          }
        } catch (e) {
          useErrorToast().add();
        }
      }
    }).open();
  }

  function deleteSelectedClip() {
    const clip = editingClip.value;
    if (!clip) {
      return false;
    }

    confirmDeleteClip(clip);
    return true;
  }

  function getFocusedMarker() {
    const activeElement = document?.activeElement;
    const focusedHandle = activeElement?.closest?.('[data-drag-clip-id][data-drag-marker-id]');
    if (!focusedHandle) {
      return null;
    }

    return findClipAndMarker({
      clipId: focusedHandle.dataset.dragClipId,
      markerId: focusedHandle.dataset.dragMarkerId
    });
  }

  function isEditableTarget(target) {
    const element = target?.nodeType === Node.ELEMENT_NODE ? target : target?.parentElement;
    if (!element) {
      return false;
    }

    const editable = element.closest?.('input, textarea, select, [contenteditable=""], [contenteditable="true"]');
    return Boolean(editable);
  }

  function handleKeydown(event) {
    if (isCustomFrameEditorOpen.value) {
      return;
    }

    if (isEditableTarget(event.target)) {
      return;
    }

    const isSpace = event.code === 'Space' || event.key === ' ';
    if (isSpace) {
      event.preventDefault();
      if (props.player?.paused?.()) {
        props.player.play?.();
      } else {
        props.player?.pause?.();
      }
      return;
    }

    if (props.disableEdit) {
      return;
    }

    const isDeleteKey = event.code === 'Delete' || event.code === 'Backspace';
    if (isDeleteKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
      const didDelete = deleteSelectedClip();
      if (didDelete) {
        event.preventDefault();
      }
      return;
    }

    const delta = event.code === 'ArrowLeft'
      ? -VIDEO_EDITOR_KEYBOARD_NUDGE_SECONDS
      : event.code === 'ArrowRight'
        ? VIDEO_EDITOR_KEYBOARD_NUDGE_SECONDS
        : 0;

    if (!delta) {
      return;
    }

    const focusedMarker = getFocusedMarker();
    if (focusedMarker) {
      event.preventDefault();
      editingClip.value = focusedMarker.clip;
      editingMarker.value = focusedMarker.marker;
      videoEditorStore.setActiveEditMarker({
        clipId: focusedMarker.clip?.id ?? null,
        markerId: focusedMarker.marker?.id ?? null
      });
      nudgeMarker(focusedMarker.marker, delta);
      return;
    }

    event.preventDefault();

    if (editingMarker.value) {
      if (editingClip.value) {
        videoEditorStore.setActiveEditMarker({
          clipId: editingClip.value?.id ?? null,
          markerId: editingMarker.value?.id ?? null
        });
      }
      nudgeMarker(editingMarker.value, delta);
      return;
    }

    if (editingClip.value) {
      nudgeClip(editingClip.value, delta);
      return;
    }

    nudgeCurrentTime(delta);
  }

  async function progressPanStart(event) {
    if (props.disableEdit) {
      return;
    }

    const srcEvent = event?.srcEvent;
    const pointerType = srcEvent?.pointerType;
    const isMouseEvent = typeof MouseEvent !== 'undefined' && srcEvent instanceof MouseEvent;
    const isPointerEvent = typeof PointerEvent !== 'undefined' && srcEvent instanceof PointerEvent;
    const isMousePointer = pointerType === 'mouse'
      || isMouseEvent
      || isPointerEvent;

    if (isMousePointer) {
      const buttons = Number(srcEvent?.buttons);
      const button = Number(srcEvent?.button);
      const isLeftButtonPressed = Number.isFinite(buttons)
        ? (buttons & 1) === 1
        : (Number.isFinite(button) ? button === 0 : true);

      if (!isLeftButtonPressed) {
        return;
      }
    }

    barRect = barRef.value.getBoundingClientRect();

    const targetElement = event?.srcEvent?.target || event?.target;
    const pointerClientX = Number(event?.center?.x ?? event?.srcEvent?.clientX ?? event?.srcEvent?.touches?.[0]?.clientX);
    const dragHandle = targetElement?.closest?.('[data-drag-clip-id][data-drag-marker-id]');
    const clipRegion = targetElement?.closest?.('[data-clip-region-id]');
    const markerThumbnail = targetElement?.closest?.('[data-marker-id]');

    dragMode = 'seek';
    draggingClip = null;
    clipDragAppliedDelta = 0;
    markerDragTimeOffsetSec = 0;

    // Marker thumbnails are no longer drag handles; only edge arrows can start marker drag.
    if (markerThumbnail && !dragHandle) {
      return;
    }

    if (dragHandle) {
      const focused = findClipAndMarker({
        clipId: dragHandle.dataset.dragClipId,
        markerId: dragHandle.dataset.dragMarkerId
      });

      if (!focused) {
        return;
      }

      const started = await startMarkerDrag({
        clip: focused.clip,
        marker: focused.marker,
        pointerClientX
      });
      if (!started) {
        return;
      }
      return;
    }

    if (clipRegion?.dataset?.clipRegionId) {
      const clip = clipsToPlot.value.find(item => item.id === clipRegion.dataset.clipRegionId);
      if (!clip) {
        return;
      }

      const edgeMarker = resolveClipEdgeMarker({
        clip,
        clipElement: clipRegion,
        pointerClientX
      });
      if (edgeMarker) {
        const started = await startMarkerDrag({
          clip,
          marker: edgeMarker,
          pointerClientX
        });
        if (!started) {
          return;
        }
        return;
      }

      selectClip({clip});
      dragMode = 'clip';
      draggingClip = clip;
      isDragging = true;
      return;
    }

    stopEdit();
    dragOffset = 0;
    dragMode = 'seek';
    syncPlayerToProgress(Math.max(0, Math.min(1, (pointerClientX - barRect.left) / barRect.width)));
    isDragging = true;
  }

  function progressPanMove(event) {
    if (!isDragging) {
      return;
    }

    if (dragMode === 'clip') {
      const duration = getDurationSec();
      if (!duration || !barRect?.width || !draggingClip) {
        return;
      }

      const requestedDelta = (event.deltaX / barRect.width) * duration;
      const incrementalDelta = requestedDelta - clipDragAppliedDelta;
      if (!incrementalDelta) {
        return;
      }

      const appliedDelta = videoEditorStore.moveClip({
        clip: draggingClip,
        delta: incrementalDelta,
        minTime: 0,
        maxTime: duration,
        refreshSnapshots: false
      });

      clipDragAppliedDelta += appliedDelta;
      if (appliedDelta !== 0) {
        queueClipSnapshotRefresh(draggingClip);
      }
      return;
    }

    const touchX = event.center.x - barRect.left;
    const ratio = Math.max(0, Math.min(1, touchX / barRect.width));

    if (dragMode === 'marker') {
      const marker = editingMarker.value;
      const duration = getDurationSec();
      if (!marker || !duration) {
        return;
      }

      const pointerTime = ratio * duration;
      const nextTime = Math.max(0, Math.min(duration, pointerTime + markerDragTimeOffsetSec));
      const didAdjust = videoEditorStore.adjustMarker({
        marker,
        time: nextTime,
        maxTime: duration,
        refreshSnapshot: false
      });

      if (didAdjust) {
        syncPlayerToProgress(marker.time / duration);
        queueMarkerSnapshotRefresh(marker);
      }

      return;
    }

    const nextProgress = Math.max(ratio + dragOffset, 0);
    syncPlayerToProgress(nextProgress);

  }

  function progressPanEnd() {
    const endedDragMode = dragMode;

    if (dragMode === 'marker' && editingMarker.value) {
      queueMarkerSnapshotRefresh.cancel();
      void videoEditorStore.refreshMarkerSnapshot({
        marker: editingMarker.value,
        time: editingMarker.value.time
      });
    }

    if (dragMode === 'clip' && draggingClip) {
      queueClipSnapshotRefresh.cancel();
      void videoEditorStore.refreshClipMarkerSnapshots({clip: draggingClip});
    }

    if (endedDragMode === 'marker' || endedDragMode === 'clip') {
      ignoreSeekClickUntilMs = Date.now() + 250;
    }

    barRect = null;
    dragOffset = 0;
    isDragging = false;
    dragMode = 'seek';
    draggingClip = null;
    clipDragAppliedDelta = 0;
    markerDragTimeOffsetSec = 0;
  }

  onMounted(() => {
    props.player?.on('timeupdate', updateProgress);
    props.player?.on('loadedmetadata', syncDurationFromPlayer);
    props.player?.on('durationchange', syncDurationFromPlayer);
    props.player?.on('loadedmetadata', updateProgress);
    document.addEventListener('keydown', handleKeydown, true);

    hammer = new Hammer(barRef.value)
    hammer.get('pan').set({ direction: Hammer.DIRECTION_HORIZONTAL, threshold: 0 })

    hammer.on('panstart', progressPanStart);
    hammer.on('panmove', progressPanMove);
    hammer.on('panend', progressPanEnd);

    syncDurationFromPlayer();
    updateProgress();
    regenerateTimelineThumbnails();
  });

  onUnmounted(() => {
    props.player?.off('timeupdate', updateProgress);
    props.player?.off('loadedmetadata', syncDurationFromPlayer);
    props.player?.off('durationchange', syncDurationFromPlayer);
    props.player?.off('loadedmetadata', updateProgress);
    document.removeEventListener('keydown', handleKeydown, true);

    if (hammer) {
      hammer.destroy()
    }

    cancelThumbnailGeneration();
    regenerateTimelineThumbnails.cancel?.();
    queueMarkerSnapshotRefresh.cancel();
    queueClipSnapshotRefresh.cancel();
    disposeTimelineThumbnails();
    videoEditorStore.clearActiveEditClip();
  });

  watch(
    [() => props.videoSrc, videoDuration, barHeight],
    () => {
      regenerateTimelineThumbnails();
    }
  );

  watch(activeEditClipId, (nextClipId, prevClipId) => {
    if (String(nextClipId) !== String(prevClipId)) {
      if (!editingMarker.value) {
        return;
      }

      const markerStillBelongsToSelectedClip = Boolean(
        editingClip.value?.markers?.some(marker =>
          marker === editingMarker.value || marker?.id === editingMarker.value?.id
        )
      );

      if (!markerStillBelongsToSelectedClip) {
        editingMarker.value = null;
      }
    }
  });

  watch(clipsToPlot, (nextClips) => {
    if (activeEditClipId.value === null || activeEditClipId.value === undefined) {
      return;
    }

    const hasSelectedClip = nextClips.some(clip => String(clip?.id) === String(activeEditClipId.value));
    if (!hasSelectedClip) {
      videoEditorStore.clearActiveEditClip();
    }
  });

</script>

<style scoped>
  @keyframes timeline-clip-loading-shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  .timeline-clip-loading {
    background-color: rgba(255, 255, 255, 0.08);
    background-image: linear-gradient(
      110deg,
      rgba(255, 255, 255, 0.06) 20%,
      rgba(255, 255, 255, 0.24) 38%,
      rgba(255, 255, 255, 0.06) 55%
    );
    background-size: 220% 100%;
    animation: timeline-clip-loading-shimmer 1.25s linear infinite;
  }
</style>
